/**
 * Preset places for the sun tools: coordinates to four decimals and the IANA zone the
 * place keeps. City coordinates are the usual city-centre values (GeoNames); a few metres
 * of difference move sunrise by well under a second.
 */

export interface Place {
  id: string;
  /** Degrees north (negative south). */
  latitude: number;
  /** Degrees east (negative west). */
  longitude: number;
  zone: string;
}

export const PLACES: readonly Place[] = [
  {
    id: "new-york",
    latitude: 40.7128,
    longitude: -74.006,
    zone: "America/New_York",
  },
  {
    id: "chicago",
    latitude: 41.8781,
    longitude: -87.6298,
    zone: "America/Chicago",
  },
  {
    id: "denver",
    latitude: 39.7392,
    longitude: -104.9903,
    zone: "America/Denver",
  },
  {
    id: "los-angeles",
    latitude: 34.0522,
    longitude: -118.2437,
    zone: "America/Los_Angeles",
  },
  {
    id: "anchorage",
    latitude: 61.2181,
    longitude: -149.9003,
    zone: "America/Anchorage",
  },
  {
    id: "honolulu",
    latitude: 21.3069,
    longitude: -157.8583,
    zone: "Pacific/Honolulu",
  },
  {
    id: "mexico-city",
    latitude: 19.4326,
    longitude: -99.1332,
    zone: "America/Mexico_City",
  },
  {
    id: "quito",
    latitude: -0.1807,
    longitude: -78.4678,
    zone: "America/Guayaquil",
  },
  {
    id: "sao-paulo",
    latitude: -23.5505,
    longitude: -46.6333,
    zone: "America/Sao_Paulo",
  },
  {
    id: "buenos-aires",
    latitude: -34.6037,
    longitude: -58.3816,
    zone: "America/Argentina/Buenos_Aires",
  },
  {
    id: "reykjavik",
    latitude: 64.1466,
    longitude: -21.9426,
    zone: "Atlantic/Reykjavik",
  },
  {
    id: "london",
    latitude: 51.5074,
    longitude: -0.1278,
    zone: "Europe/London",
  },
  { id: "paris", latitude: 48.8566, longitude: 2.3522, zone: "Europe/Paris" },
  { id: "berlin", latitude: 52.52, longitude: 13.405, zone: "Europe/Berlin" },
  { id: "tromso", latitude: 69.6492, longitude: 18.9553, zone: "Europe/Oslo" },
  {
    id: "moscow",
    latitude: 55.7558,
    longitude: 37.6173,
    zone: "Europe/Moscow",
  },
  { id: "cairo", latitude: 30.0444, longitude: 31.2357, zone: "Africa/Cairo" },
  { id: "lagos", latitude: 6.5244, longitude: 3.3792, zone: "Africa/Lagos" },
  {
    id: "nairobi",
    latitude: -1.2921,
    longitude: 36.8219,
    zone: "Africa/Nairobi",
  },
  {
    id: "johannesburg",
    latitude: -26.2041,
    longitude: 28.0473,
    zone: "Africa/Johannesburg",
  },
  { id: "dubai", latitude: 25.2048, longitude: 55.2708, zone: "Asia/Dubai" },
  { id: "mumbai", latitude: 19.076, longitude: 72.8777, zone: "Asia/Kolkata" },
  {
    id: "singapore",
    latitude: 1.3521,
    longitude: 103.8198,
    zone: "Asia/Singapore",
  },
  {
    id: "beijing",
    latitude: 39.9042,
    longitude: 116.4074,
    zone: "Asia/Shanghai",
  },
  { id: "tokyo", latitude: 35.6762, longitude: 139.6503, zone: "Asia/Tokyo" },
  {
    id: "sydney",
    latitude: -33.8688,
    longitude: 151.2093,
    zone: "Australia/Sydney",
  },
  {
    id: "auckland",
    latitude: -36.8485,
    longitude: 174.7633,
    zone: "Pacific/Auckland",
  },
];

export function findPlace(id: string): Place | null {
  return PLACES.find((place) => place.id === id) ?? null;
}

/** IANA zones offered in the zone menu: every preset's zone plus a few common extras. */
export const ZONE_IDS: readonly string[] = [
  ...new Set([
    "UTC",
    ...PLACES.map((place) => place.zone),
    "America/Toronto",
    "America/Phoenix",
    "America/Bogota",
    "America/Halifax",
    "Europe/Dublin",
    "Europe/Lisbon",
    "Europe/Madrid",
    "Europe/Rome",
    "Europe/Istanbul",
    "Europe/Athens",
    "Asia/Karachi",
    "Asia/Dhaka",
    "Asia/Bangkok",
    "Asia/Hong_Kong",
    "Asia/Seoul",
    "Australia/Perth",
    "Australia/Adelaide",
  ]),
].sort((a, b) => a.localeCompare(b));
